import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import cn from 'clsx'
import { createPortal } from 'react-dom'

import { useOnClickOutside } from '@App/hooks'

import s from './styles.module.scss'

type PopoverPosition = 'top' | 'bottom' | 'left' | 'right'
type PopoverAlign = 'start' | 'center' | 'end'

type PopoverProps = {
  trigger?: React.ReactNode
  children: React.ReactNode | ((onClose: () => void) => React.ReactNode)
  position?: PopoverPosition
  align?: PopoverAlign
  offset?: number
  className?: string
  contentClassName?: string
  closeOnClickOutside?: boolean
  closeOnSelect?: boolean
  closeOnScroll?: boolean
  renderInPortal?: boolean
  onUnmount?: () => void
  isOpen?: boolean
  setIsOpen?: (val: boolean | ((val: boolean) => boolean)) => void
}

export const Popover = ({
  trigger,
  children,
  position = 'bottom',
  align = 'end',
  offset = 8,
  className,
  contentClassName,
  closeOnClickOutside = true,
  closeOnSelect = true,
  closeOnScroll = false,
  renderInPortal = true,
  onUnmount,
  isOpen: isOpenProp,
  setIsOpen: setIsOpenProp,
}: PopoverProps) => {
  const [_isOpen, _setIsOpen] = useState(!!isOpenProp)
  const isOpen = isOpenProp !== undefined ? isOpenProp : _isOpen
  const setIsOpen = (val: boolean | ((val: boolean) => boolean)) => {
    setIsOpenProp?.(val)
    _setIsOpen(val)
  }
  const [coords, setCoords] = useState({ top: 0, left: 0 })

  useEffect(() => {
    return () => onUnmount?.()
  }, [])

  const triggerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const updatePosition = () => {
    if (!triggerRef.current || !contentRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const contentRect = contentRef.current.getBoundingClientRect()

    let top = 0
    let left = 0

    switch (position) {
      case 'top':
        top = triggerRect.top - contentRect.height - offset
        break
      case 'bottom':
        top = triggerRect.bottom + offset
        break
      case 'left':
        left = triggerRect.left - contentRect.width - offset
        break
      case 'right':
        left = triggerRect.right + offset
        break
    }

    if (position === 'top' || position === 'bottom') {
      switch (align) {
        case 'start':
          left = triggerRect.left
          break
        case 'center':
          left = triggerRect.left + (triggerRect.width - contentRect.width) / 2
          break
        case 'end':
          left = triggerRect.right - contentRect.width
          break
      }
    } else {
      switch (align) {
        case 'start':
          top = triggerRect.top
          break
        case 'center':
          top = triggerRect.top + (triggerRect.height - contentRect.height) / 2
          break
        case 'end':
          top = triggerRect.bottom - contentRect.height
          break
      }
    }

    const padding = 8
    if (left < padding) left = padding
    if (top < padding) top = padding
    if (left + contentRect.width > window.innerWidth - padding) {
      left = window.innerWidth - contentRect.width - padding
    }
    if (top + contentRect.height > window.innerHeight - padding) {
      top = window.innerHeight - contentRect.height - padding
    }

    setCoords({ top, left })
  }

  useLayoutEffect(() => {
    if (isOpen && renderInPortal) {
      updatePosition()

      const handleScroll = () => {
        if (closeOnScroll) {
          setIsOpen(false)
        } else {
          updatePosition()
        }
      }

      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', updatePosition)

      return () => {
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [isOpen, closeOnScroll, renderInPortal])

  useOnClickOutside(contentRef, (e: Event) => {
    if (closeOnClickOutside && !triggerRef.current?.contains(e.target as Node)) {
      setIsOpen(false)
    }
  })

  const handleToggle = () => {
    if (isOpenProp !== undefined) return
    setIsOpen((prev) => !prev)
  }

  const handleContentClick = () => {
    if (closeOnSelect) {
      setIsOpen(false)
    }
  }

  const content = (
    <div
      ref={contentRef}
      className={cn(s.content, contentClassName)}
      style={renderInPortal ? { top: coords.top, left: coords.left } : undefined}
      onClick={handleContentClick}
    >
      {typeof children === 'function' ? children(() => setIsOpen(false)) : children}
    </div>
  )

  return (
    <>
      <div ref={triggerRef} className={cn(s.trigger, className)} onClick={handleToggle}>
        {trigger}
      </div>

      {isOpen && (renderInPortal ? createPortal(content, document.body) : content)}
    </>
  )
}

type PopoverMenuProps = {
  children: React.ReactNode
  className?: string
}

export const PopoverMenu = ({ children, className }: PopoverMenuProps) => {
  return <div className={cn(s.menu, className)}>{children}</div>
}

type PopoverMenuItemProps = {
  children: React.ReactNode
  onClick?: () => void
  className?: string
  danger?: boolean
}

export const PopoverMenuItem = ({ children, onClick, className, danger }: PopoverMenuItemProps) => {
  return (
    <div className={cn(s.menuItem, danger && s.danger, className)} onClick={onClick}>
      {children}
    </div>
  )
}
